import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Images, X } from 'lucide-react';
import { BlurredMedia } from '@/components/BlurredMedia';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { getImageUrl } from '@/lib/utils/image';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { HStack } from '@/components/core';

export interface GalleryImage {
  id: number;
  url: string;
  thumbnail_url?: string;
  media_type?: string;
  category?: string;
  title?: string;
  description?: string;
  is_premium?: boolean;
  is_fsk18?: boolean;
  is_profile_image?: boolean;
  sort_order?: number;
  unlock_price?: number;
  should_blur?: boolean;
}

interface StickyMiniGalleryProps {
  /** Name shown in the mini gallery bar */
  name: string;
  /** Gallery images to display */
  galleryImages: GalleryImage[];
  /** Full character data for the expanded gallery view */
  character: {
    id: number;
    name: string;
    avatar_url?: string;
    gallery?: GalleryImage[];
  };
  /** Ref to the element that triggers show/hide (typically the full gallery section) */
  triggerRef: React.RefObject<HTMLElement>;
  /** Whether to show on mobile (default: false - desktop only) */
  showOnMobile?: boolean;
  /** Custom offset from left for sidebar layouts (e.g., "352px" for 320px sidebar + 32px gap) */
  sidebarOffset?: string;
  /** Custom class names */
  className?: string;
}

export const StickyMiniGallery: React.FC<StickyMiniGalleryProps> = ({
  name,
  galleryImages,
  character,
  triggerRef,
  showOnMobile = false,
  sidebarOffset = '352px',
  className,
}) => {
  const { t } = useTranslation('characters');
  const [showMiniGallery, setShowMiniGallery] = useState(false);
  
  // Lightbox state
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [selectedImageNsfw, setSelectedImageNsfw] = useState<boolean>(false);
  const [selectedImageShouldBlur, setSelectedImageShouldBlur] = useState<boolean>(false);
  
  // Touch state for swipe gestures
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  // Filter images for display (exclude chat/creator categories and profile images)
  const displayImages = galleryImages
    .filter((img) => {
      const category = (img.category || '').toLowerCase();
      return category !== 'chat' && category !== 'creator' && !img.is_profile_image;
    })
    .slice(0, 8);

  // Track when trigger element scrolls out of view using IntersectionObserver
  useEffect(() => {
    if (!triggerRef.current) return;

    // Desktop hat Top-Navigation (96px), Mobile nicht (0px)
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const topOffset = isDesktop ? '-96px' : '0px';

    // Flag um initiales Laden zu ignorieren - Mini-Galerie erst nach erstem "sichtbar" zeigen
    let hasBeenVisible = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (entry.isIntersecting) {
          // Element ist sichtbar - merken, dass es sichtbar war
          hasBeenVisible = true;
          setShowMiniGallery(false);
        } else if (hasBeenVisible) {
          // Element ist nicht mehr sichtbar - prüfen ob es NACH OBEN verschwunden ist
          // boundingClientRect.bottom < 0 bedeutet: Element ist komplett über dem Viewport
          const isScrolledUp = entry.boundingClientRect.bottom < (isDesktop ? 96 : 0);
          setShowMiniGallery(isScrolledUp);
        }
        // Wenn hasBeenVisible false ist und nicht intersecting → ignorieren (initiales Laden)
      },
      {
        root: null,
        rootMargin: `${topOffset} 0px 0px 0px`,
        threshold: 0
      }
    );

    observer.observe(triggerRef.current);

    return () => observer.disconnect();
  }, [triggerRef]);

  // Navigate to prev/next image
  const navigateImage = useCallback((direction: 'prev' | 'next'): void => {
    if (displayImages.length === 0) return;
    
    let newIndex = selectedImageIndex;
    if (direction === 'prev') {
      newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : displayImages.length - 1;
    } else {
      newIndex = selectedImageIndex < displayImages.length - 1 ? selectedImageIndex + 1 : 0;
    }
    
    const newImage = displayImages[newIndex];
    if (newImage) {
      setSelectedImageIndex(newIndex);
      setSelectedImage(getImageUrl(newImage.url));
      setSelectedImageNsfw(Boolean(newImage.is_fsk18));
      setSelectedImageShouldBlur(Boolean(newImage.should_blur));
    }
  }, [displayImages, selectedImageIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (!selectedImage || displayImages.length <= 1) return;
      
      if (e.key === 'ArrowLeft') {
        navigateImage('prev');
      } else if (e.key === 'ArrowRight') {
        navigateImage('next');
      } else if (e.key === 'Escape') {
        setSelectedImage(null);
      }
    };
    
    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage, displayImages.length, navigateImage]);

  // Touch handlers for swipe gestures
  const onTouchStart = (e: React.TouchEvent): void => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0] ? e.targetTouches[0].clientX : null);
  };
  
  const onTouchMove = (e: React.TouchEvent): void => {
    setTouchEnd(e.targetTouches[0] ? e.targetTouches[0].clientX : null);
  };
  
  const onTouchEnd = (): void => {
    if (touchStart == null || touchEnd == null) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && displayImages.length > 1) {
      navigateImage('next');
    }
    if (isRightSwipe && displayImages.length > 1) {
      navigateImage('prev');
    }
  };

  // Open lightbox with first image
  const openLightbox = (): void => {
    const firstImage = displayImages[0];
    if (firstImage) {
      setSelectedImageIndex(0);
      setSelectedImage(getImageUrl(firstImage.url));
      setSelectedImageNsfw(Boolean(firstImage.is_fsk18));
      setSelectedImageShouldBlur(Boolean(firstImage.should_blur));
    }
  };

  // Don't render if no gallery images
  if (galleryImages.length === 0) return null;
  if (displayImages.length === 0) return null;

  return (
    <>
      {/* ===== FIXED MINI GALLERY BAR ===== */}
      <div
        className={cn(
          // Desktop: unter der Navigation (top-24), Mobile: ganz oben (top-0)
          "fixed left-0 right-0 z-40 transition-all duration-500 ease-out",
          "top-0 md:top-24",
          !showOnMobile && "max-md:hidden",
          showMiniGallery && !selectedImage
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-full pointer-events-none",
          className
        )}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          {/* Mobile: volle Breite, Desktop: Offset für Sidebar */}
          <div className="md:ml-[312px] lg:ml-[352px]">
            {/* Premium glass panel - matching mini-filter design */}
            <div className="relative rounded-2xl overflow-hidden">
              {/* Outer glow effect */}
              <div className="absolute -inset-[1px] bg-gradient-to-b from-white/15 via-white/5 to-transparent rounded-2xl blur-sm opacity-50" />

              {/* Main panel */}
              <div
                className="relative bg-black/40 backdrop-blur-2xl border border-white/[0.08] rounded-2xl shadow-token-modal shadow-black/30 p-token-sm cursor-pointer hover:bg-black/50 transition-colors"
                onClick={openLightbox}
              >
                {/* Top highlight line */}
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
              <HStack gap="md" align="center">
                {/* Mini Thumbnails */}
                <HStack className="[&>*:not(:first-child)]:-ml-3">
                  {displayImages.slice(0, 5).map((img, idx) => (
                    <div
                      key={img.id || idx}
                      className="w-10 h-10 rounded-token-button overflow-hidden ring-2 ring-[hsl(var(--background))] shadow-token-dropdown transition-transform hover:scale-110 hover:z-10"
                      style={{ zIndex: 5 - idx }}
                    >
                      <BlurredMedia
                        src={getImageUrl(img.thumbnail_url || img.url)}
                        alt=""
                        shouldBlur={Boolean(img.should_blur)}
                        isFsk18={Boolean(img.is_fsk18)}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  {displayImages.length > 5 && (
                    <div className="w-10 h-10 rounded-token-button bg-[hsl(var(--brand-primary))]/20 ring-2 ring-[hsl(var(--background))] flex items-center justify-center">
                      <span className="text-xs font-bold" style={{ color: 'hsl(var(--brand-primary))' }}>
                        +{displayImages.length - 5}
                      </span>
                    </div>
                  )}
                </HStack>

                {/* Label */}
                <HStack gap="sm" align="center" className="flex-1 min-w-0">
                  <Images className="h-4 w-4 flex-shrink-0" style={{ color: 'hsl(var(--brand-primary))' }} />
                  <span className="text-sm font-medium design-text-primary truncate">
                    {name} {t('profile.gallery', { defaultValue: 'Galerie' })}
                  </span>
                  <span className="text-xs design-text-secondary whitespace-nowrap">
                    ({displayImages.length} {t('profile.images', { defaultValue: 'Bilder' })})
                  </span>
                </HStack>

                {/* Expand hint */}
                <ChevronDown className="h-5 w-5 design-text-secondary flex-shrink-0" />
              </HStack>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== IMAGE LIGHTBOX MODAL ===== */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="modal-dialog-content bg-background/95 backdrop-blur-md border-white/20 p-0 overflow-hidden [&>button]:hidden" aria-describedby="mini-gallery-image-modal-description">
          <DialogTitle className="sr-only">{t('profile.galleryImageAlt', { name: character.name })}</DialogTitle>
          <div className="sr-only">
            <p id="mini-gallery-image-modal-description">{t('profile.imageModalDescription', { current: selectedImageIndex + 1, total: displayImages.length })}</p>
          </div>
          <div 
            className="modal-image-container"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Navigation Arrows */}
            {displayImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateImage('prev')}
                  className="modal-navigation-button absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white bg-black/50 hover:bg-black/70 h-12 w-12"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateImage('next')}
                  className="modal-navigation-button absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white bg-black/50 hover:bg-black/70 h-12 w-12"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}
            
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 text-white bg-black/50 hover:bg-black/70"
            >
              <X className="h-6 w-6" />
            </Button>
            
            {/* Image or Video */}
            <div className="relative w-full h-full flex items-center justify-center p-token-md">
              {selectedImage && (
                <BlurredMedia
                  src={selectedImage}
                  alt={character.name}
                  shouldBlur={selectedImageShouldBlur}
                  isFsk18={selectedImageNsfw}
                  className="modal-image"
                  autoPlayVideo
                  showMuteToggle
                />
              )}
            </div>
            
            {/* Swipe indicator dots */}
            {displayImages.length > 1 && (
              <div className="swipe-indicators">
                {displayImages.map((_, index) => (
                  <div 
                    key={index}
                    className={cn(
                      "swipe-dot",
                      index === selectedImageIndex && "active"
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
